import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft, Save, CircleDot } from 'lucide-react'

const API = ''

interface Options {
  brands: { id: string; slug: string; name: string }[]
  categories: { id: string; slug: string; name: string }[]
  patterns: { id: string; name: string; tireBrandId: string }[]
  suppliers: { id: string; name: string }[]
  constants: {
    seasons: { value: string; label: string }[]
    speedRatings: string[]
    widths: number[]
    aspectRatios: number[]
    rimDiameters: number[]
  }
}

interface Props {
  mode: 'create' | 'edit'
}

interface FormState {
  sku: string
  name: string
  description: string
  price: string
  salePrice: string
  stock: string
  image: string
  isActive: boolean

  tireBrandId: string
  tireCategoryId: string
  tirePatternId: string
  width: string
  aspectRatio: string
  rimDiameter: string
  loadIndex: string
  speedRating: string
  season: 'all' | 'summer' | 'winter' | 'off_road'
  reinforced: boolean
  runFlat: boolean
  selfSeal: boolean
  electricVehicle: boolean
  warrantyKm: string
  warrantyYears: string
  dotCode: string
  manufactureYear: string
}

const blank: FormState = {
  sku: '',
  name: '',
  description: '',
  price: '',
  salePrice: '',
  stock: '0',
  image: '',
  isActive: true,
  tireBrandId: '',
  tireCategoryId: '',
  tirePatternId: '',
  width: '205',
  aspectRatio: '55',
  rimDiameter: '16',
  loadIndex: '',
  speedRating: '',
  season: 'all',
  reinforced: false,
  runFlat: false,
  selfSeal: false,
  electricVehicle: false,
  warrantyKm: '',
  warrantyYears: '',
  dotCode: '',
  manufactureYear: '',
}

export function LlantaForm({ mode }: Props) {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { id?: string }
  const [opts, setOpts] = useState<Options | null>(null)
  const [f, setF] = useState<FormState>(blank)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetch(`${API}/api/admin/llantas/options`)
      .then((r) => r.json())
      .then(setOpts)
  }, [])

  useEffect(() => {
    if (mode !== 'edit' || !params.id) return
    setLoading(true)
    fetch(`${API}/api/admin/llantas/${params.id}`)
      .then((r) => r.json())
      .then((d) => {
        if (!d.spec) return
        setF({
          sku: d.sku,
          name: d.name,
          description: d.description || '',
          price: String(d.price),
          salePrice: d.salePrice ? String(d.salePrice) : '',
          stock: String(d.stock),
          image: d.image || '',
          isActive: d.isActive,
          tireBrandId: d.spec.tireBrandId,
          tireCategoryId: d.spec.tireCategoryId,
          tirePatternId: d.spec.tirePatternId || '',
          width: String(d.spec.width),
          aspectRatio: String(d.spec.aspectRatio),
          rimDiameter: String(d.spec.rimDiameter),
          loadIndex: d.spec.loadIndex ? String(d.spec.loadIndex) : '',
          speedRating: d.spec.speedRating || '',
          season: d.spec.season,
          reinforced: d.spec.reinforced,
          runFlat: d.spec.runFlat,
          selfSeal: d.spec.selfSeal,
          electricVehicle: d.spec.electricVehicle,
          warrantyKm: d.spec.warrantyKm ? String(d.spec.warrantyKm) : '',
          warrantyYears: d.spec.warrantyYears ? String(d.spec.warrantyYears) : '',
          dotCode: d.spec.dotCode || '',
          manufactureYear: d.spec.manufactureYear ? String(d.spec.manufactureYear) : '',
        })
      })
      .finally(() => setLoading(false))
  }, [mode, params.id])

  function set<K extends keyof FormState>(key: K, val: FormState[K]) {
    setF((s) => ({ ...s, [key]: val }))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const payload: any = {
      name: f.name,
      description: f.description,
      price: parseFloat(f.price),
      salePrice: f.salePrice ? parseFloat(f.salePrice) : null,
      stock: parseInt(f.stock) || 0,
      image: f.image,
      isActive: f.isActive,
      tireBrandId: f.tireBrandId,
      tireCategoryId: f.tireCategoryId,
      tirePatternId: f.tirePatternId || null,
      width: parseInt(f.width),
      aspectRatio: parseInt(f.aspectRatio),
      rimDiameter: parseInt(f.rimDiameter),
      loadIndex: f.loadIndex ? parseInt(f.loadIndex) : null,
      speedRating: f.speedRating || null,
      season: f.season,
      reinforced: f.reinforced,
      runFlat: f.runFlat,
      selfSeal: f.selfSeal,
      electricVehicle: f.electricVehicle,
      warrantyKm: f.warrantyKm ? parseInt(f.warrantyKm) : null,
      warrantyYears: f.warrantyYears ? parseInt(f.warrantyYears) : null,
      dotCode: f.dotCode || null,
      manufactureYear: f.manufactureYear ? parseInt(f.manufactureYear) : null,
    }
    if (mode === 'create') payload.sku = f.sku

    const url = mode === 'create'
      ? `${API}/api/admin/llantas`
      : `${API}/api/admin/llantas/${params.id}`
    const method = mode === 'create' ? 'POST' : 'PUT'

    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    setLoading(false)

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      setError(err.error || `HTTP ${res.status}`)
      return
    }
    navigate({ to: '/llantas' })
  }

  const patternsForBrand = opts?.patterns.filter((p) => p.tireBrandId === f.tireBrandId) ?? []

  return (
    <form onSubmit={submit} className="space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: '/llantas' })}
            className="rounded-lg border border-ink-200 bg-white p-2 hover:bg-ink-50"
          >
            <ArrowLeft className="size-4" />
          </button>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
            <CircleDot className="size-6 text-brand-600" />
            {mode === 'create' ? 'Nueva llanta' : 'Editar llanta'}
          </h1>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
        >
          <Save className="size-4" /> {loading ? 'Guardando…' : 'Guardar'}
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-500">
          Datos comerciales
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="SKU *">
            <input
              required
              disabled={mode === 'edit'}
              value={f.sku}
              onChange={(e) => set('sku', e.target.value.toUpperCase())}
              className="form-input"
              placeholder="BRG-EP150-2055516"
            />
          </Field>
          <Field label="Nombre *">
            <input
              required
              value={f.name}
              onChange={(e) => set('name', e.target.value)}
              className="form-input"
              placeholder="Bridgestone Ecopia EP150 205/55R16 91V"
            />
          </Field>
          <Field label="Precio (MXN) *">
            <input
              required
              type="number"
              step="0.01"
              value={f.price}
              onChange={(e) => set('price', e.target.value)}
              className="form-input"
            />
          </Field>
          <Field label="Precio de oferta (opcional)">
            <input
              type="number"
              step="0.01"
              value={f.salePrice}
              onChange={(e) => set('salePrice', e.target.value)}
              className="form-input"
            />
          </Field>
          <Field label="Stock">
            <input
              type="number"
              value={f.stock}
              onChange={(e) => set('stock', e.target.value)}
              className="form-input"
            />
          </Field>
          <Field label="Imagen (URL o nombre archivo)">
            <input
              value={f.image}
              onChange={(e) => set('image', e.target.value)}
              className="form-input"
              placeholder="brg-ep150.jpg"
            />
          </Field>
          <Field label="Descripción" full>
            <textarea
              rows={3}
              value={f.description}
              onChange={(e) => set('description', e.target.value)}
              className="form-input"
              placeholder="Descripción comercial visible al cliente"
            />
          </Field>
          <Field label="Estado">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={f.isActive}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              Activa (visible en sitio)
            </label>
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ink-500">
          Datos técnicos
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Marca *">
            <select
              required
              value={f.tireBrandId}
              onChange={(e) => set('tireBrandId', e.target.value)}
              className="form-input"
            >
              <option value="">— Selecciona —</option>
              {opts?.brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Sección *">
            <select
              required
              value={f.tireCategoryId}
              onChange={(e) => set('tireCategoryId', e.target.value)}
              className="form-input"
            >
              <option value="">— Selecciona —</option>
              {opts?.categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Línea / modelo (opcional)">
            <select
              value={f.tirePatternId}
              onChange={(e) => set('tirePatternId', e.target.value)}
              className="form-input"
              disabled={patternsForBrand.length === 0}
            >
              <option value="">—</option>
              {patternsForBrand.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Ancho *">
            <input
              required
              type="number"
              value={f.width}
              onChange={(e) => set('width', e.target.value)}
              className="form-input"
              list="widths-list"
            />
            <datalist id="widths-list">
              {opts?.constants.widths.map((w) => <option key={w} value={w} />)}
            </datalist>
          </Field>
          <Field label="Perfil *">
            <input
              required
              type="number"
              value={f.aspectRatio}
              onChange={(e) => set('aspectRatio', e.target.value)}
              className="form-input"
              list="aspect-list"
            />
            <datalist id="aspect-list">
              {opts?.constants.aspectRatios.map((a) => <option key={a} value={a} />)}
            </datalist>
          </Field>
          <Field label="Rin *">
            <input
              required
              type="number"
              value={f.rimDiameter}
              onChange={(e) => set('rimDiameter', e.target.value)}
              className="form-input"
              list="rim-list"
            />
            <datalist id="rim-list">
              {opts?.constants.rimDiameters.map((r) => <option key={r} value={r} />)}
            </datalist>
          </Field>

          <Field label="Índice de carga">
            <input
              type="number"
              value={f.loadIndex}
              onChange={(e) => set('loadIndex', e.target.value)}
              className="form-input"
              placeholder="91"
            />
          </Field>
          <Field label="Índice de velocidad">
            <select
              value={f.speedRating}
              onChange={(e) => set('speedRating', e.target.value)}
              className="form-input"
            >
              <option value="">—</option>
              {opts?.constants.speedRatings.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Estación">
            <select
              value={f.season}
              onChange={(e) => set('season', e.target.value as any)}
              className="form-input"
            >
              {opts?.constants.seasons.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Garantía (km)">
            <input
              type="number"
              value={f.warrantyKm}
              onChange={(e) => set('warrantyKm', e.target.value)}
              className="form-input"
              placeholder="80000"
            />
          </Field>
          <Field label="Garantía (años)">
            <input
              type="number"
              value={f.warrantyYears}
              onChange={(e) => set('warrantyYears', e.target.value)}
              className="form-input"
              placeholder="5"
            />
          </Field>
          <Field label="Año fabricación">
            <input
              type="number"
              value={f.manufactureYear}
              onChange={(e) => set('manufactureYear', e.target.value)}
              className="form-input"
              placeholder="2024"
            />
          </Field>
          <Field label="DOT">
            <input
              value={f.dotCode}
              onChange={(e) => set('dotCode', e.target.value)}
              className="form-input"
              placeholder="DOT-MX-2418"
            />
          </Field>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {([
            ['reinforced', 'Reforzada (XL)'],
            ['runFlat', 'Run-flat'],
            ['selfSeal', 'Auto-sellante'],
            ['electricVehicle', 'Para EV / Híbrido'],
          ] as const).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={f[k]}
                onChange={(e) => set(k, e.target.checked)}
              />
              {label}
            </label>
          ))}
        </div>
      </section>
    </form>
  )
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={`flex flex-col gap-1 text-sm ${full ? 'sm:col-span-2 lg:col-span-3' : ''}`}>
      <span className="font-medium text-ink-700">{label}</span>
      {children}
    </label>
  )
}
